<!-- Polaris (fase 5.2): what acts on the selected contacts, on the kit's popover surface (the 3.1 bulk bar:
     bg-polaris-popover, border, shadow-md) and the kit's BulkActions order (areas/contacts/BulkActions.vue): the
     count, then the actions. It sits above the list, under the page head (the table's header row sticks under it,
     never behind it), and wraps to two rows when the list is narrow. Same actions as the woot: select the page,
     clear, assign and remove labels, delete (the page asks first). The Polaris AI BulkSelectBar is no longer used
     here. -->
<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { Checkmark16, Subtract16, TrashCan16 } from '@carbon/icons-vue';

import { Button } from 'dashboard/components-next/polaris/ui/button';
import { Checkbox } from 'dashboard/components-next/polaris/ui/checkbox';
import BulkLabelActions from 'dashboard/components/widgets/conversation/conversationBulkActions/BulkLabelActions.vue';
import Policy from 'dashboard/components/policy.vue';

const props = defineProps({
  visibleContactIds: {
    type: Array,
    default: () => [],
  },
  selectedContactIds: {
    type: Array,
    default: () => [],
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  'clearSelection',
  'assignLabels',
  'removeLabels',
  'toggleAll',
  'deleteSelected',
]);

const { t } = useI18n();

const selectedCount = computed(() => props.selectedContactIds.length);

const pageState = computed(() => {
  const selected = new Set(props.selectedContactIds);
  const picked = props.visibleContactIds.filter(id => selected.has(id)).length;
  if (!picked) return false;
  return picked === props.visibleContactIds.length ? true : 'indeterminate';
});

const selectAllLabel = computed(() =>
  t('CONTACTS_BULK_ACTIONS.SELECT_ALL', {
    count: props.visibleContactIds.length,
  })
);
</script>

<template>
  <div
    role="group"
    :aria-label="t('CONTACTS_POLARIS.BULK.LABEL')"
    class="flex shrink-0 flex-wrap items-center justify-between gap-2 border-0 border-b border-solid border-polaris-border px-6 py-2"
    data-contacts-bulk-bar
  >
    <div
      class="flex w-full flex-wrap items-center justify-between gap-2 rounded-polaris-lg border border-solid border-polaris-border bg-polaris-popover p-2 text-polaris-popover-foreground shadow-md"
    >
      <div class="flex min-w-0 items-center gap-1.5 ps-0.5">
        <label class="flex min-w-0 cursor-pointer items-center gap-2">
          <Checkbox
            v-slot="{ state }"
            :model-value="pageState"
            :aria-label="selectAllLabel"
            @update:model-value="value => emit('toggleAll', value === true)"
          >
            <Subtract16 v-if="state === 'indeterminate'" class="size-3.5" />
            <Checkmark16 v-else class="size-3.5" />
          </Checkbox>
          <span
            class="truncate font-medium tabular-nums type-polaris-body"
            aria-live="polite"
          >
            {{
              t('CONTACTS_BULK_ACTIONS.SELECTED_COUNT', {
                count: selectedCount,
              })
            }}
          </span>
        </label>
        <div
          aria-hidden="true"
          class="mx-1 h-3 w-px shrink-0 rounded-polaris-sm bg-polaris-border"
        />
        <Button variant="ghost" size="xs" @click="emit('clearSelection')">
          {{ t('CONTACTS_BULK_ACTIONS.CLEAR_SELECTION') }}
        </Button>
      </div>
      <div class="flex flex-wrap items-center gap-1">
        <BulkLabelActions
          type="contact"
          :is-loading="isLoading"
          :disabled="!selectedCount"
          @assign="labels => emit('assignLabels', labels)"
        />
        <BulkLabelActions
          type="contact"
          action="remove"
          :is-loading="isLoading"
          :disabled="!selectedCount"
          @remove="labels => emit('removeLabels', labels)"
        />
        <Policy :permissions="['administrator']" class="flex items-center">
          <div
            aria-hidden="true"
            class="mx-1 h-3 w-px rounded-polaris-sm bg-polaris-border"
          />
          <Button
            variant="ghost"
            size="sm"
            class="text-polaris-destructive hover:bg-polaris-destructive/10 hover:text-polaris-destructive"
            :disabled="!selectedCount || isLoading"
            @click="emit('deleteSelected')"
          >
            <TrashCan16 aria-hidden="true" />
            {{ t('CONTACTS_BULK_ACTIONS.DELETE_CONTACTS') }}
          </Button>
        </Policy>
      </div>
    </div>
  </div>
</template>
